import { Location } from '@angular/common';
import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MaxBrainExperience } from 'app/library/experience/models/experience.model';
import { ExperienceService } from 'app/library/experience/services/experience.service';
import { SidenavService } from 'app/library/grow/services/sidenav.service';
import { SelectMxImageLibraryDialogComponent } from 'app/library/image-library/dialogs/select-mx-image/select-mx-image.component';
import { ModuleImage } from 'app/library/module/models/module-image.model';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { MaxBrainEditorDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/editor/editor.component';
import { IMaxBrainEnvironment } from 'app/projects/shared/src/lib/interfaces/environment.interface';
import { ILanguageService } from 'app/projects/shared/src/lib/interfaces/language.interface';
import { FilterLanguagesPipe } from 'app/projects/shared/src/lib/pipes/filter-languages.pipe';
import { MAXBRAIN_LANGUAGE_SERVICE } from 'app/projects/shared/src/lib/services/language.token';
import { removeDataAttributesFromElement, removeHtmlDataAttributes } from 'app/library/grow/utils/remove-html-data-attributes.util';
import { MaxBrainCustomTranslateService } from 'app/services/custom-translate.service';
import { IComponentCanDiscardChanges } from 'app/services/discard-changes.guard';
import { TinymceService } from 'app/services/tinymce.service';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-experience-home',
    templateUrl: './experience-home.component.html',
    styleUrls: ['./experience-home.component.scss'],
})
export class ExperienceHomeComponent extends UnsubscribeOnDestroy implements OnInit, IComponentCanDiscardChanges {
    experienceDescriptionTranslations: { key: string };
    experienceTitleTranslations: { key: string };
    unsplashViewsBaseUrl: string;
    growKeywords: string[] = [];

    get title() {
        return this.formGroup.get('title');
    }
    get description() {
        return this.formGroup.get('description');
    }

    private _formGroup: FormGroup;
    experienceId: string;
    selectedExperienceLanguageId: string;

    get isDataChanged(): boolean {
        return !this.formGroup || this.formGroup.dirty;
    }

    get formGroup(): FormGroup {
        return this._formGroup;
    }

    isImageUpdated = false;
    experience: MaxBrainExperience;
    imageLibraryDialogRef: MatDialogRef<SelectMxImageLibraryDialogComponent>;
    discardChangesDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    editorDialogRef: MatDialogRef<MaxBrainEditorDialogComponent>;

    screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    @HostListener('window:resize')
    onResize() {
        this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    }

    constructor(
        @Inject(MAXBRAIN_ENVIRONMENT) private _environment: IMaxBrainEnvironment,
        @Inject(MAXBRAIN_LANGUAGE_SERVICE) public languageService: ILanguageService,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar,
        private _experienceService: ExperienceService,
        private _route: ActivatedRoute,
        private _matDialog: MatDialog,
        private _location: Location,
        private _formBuilder: FormBuilder,
        private _customTranslateService: MaxBrainCustomTranslateService,
        public sidenavService: SidenavService,
        private _filterLanguagesPipe: FilterLanguagesPipe,
        private _tinymceService: TinymceService
    ) {
        super();
        this.unsplashViewsBaseUrl = this._environment.unsplashViewsBaseUrl;
    }

    async ngOnInit(): Promise<void> {
        this.experienceId = this._route.parent.snapshot.params['id'];
        this.experience = this._experienceService.getExperienceById(this.experienceId);

        this._formGroup = this._formBuilder.group({
            language: this._formBuilder.array([]),
        });

        const selectedLanguages = this._filterLanguagesPipe.transform(this.languageService.languages);
        this.setExperienceLanguagesForm(selectedLanguages[0].id);

        this._experienceService.saveExperienceChangesSubject$.pipe(takeUntil(this._unsubscribeAll)).subscribe((saveChanges) => {
            if (saveChanges) {
                this.saveChanges();
                this._experienceService.saveExperienceChanges(false);
            }
        });

        this.growKeywords = await this._experienceService.fetchGrowKeywords();
    }

    async setExperienceLanguagesForm(id: string) {
        this.changeExperienceLanguage(id);
        this.selectedExperienceLanguageId = 'translation_' + id;
        this.experienceTitleTranslations = await this._customTranslateService.getTranslationsByKey(this.experience.headerBanner.title);
        this.experienceDescriptionTranslations = await this._customTranslateService.getTranslationsByKey(this.experience.headerBanner.description);

        if (this.experience) {
            const languageControl = <FormArray>this._formGroup.get('language');

            Object.keys(this.experienceTitleTranslations).forEach((key) => {
                languageControl.push(
                    this._formBuilder.group({
                        id: [key, Validators.required],
                        title: [this.decodeHtmlEntities(this.experienceTitleTranslations[key]), Validators.required],
                        description: [this.decodeHtmlEntities(this.experienceDescriptionTranslations[key])],
                    })
                );
            });
        }
    }

    copyKeywordToClipboard(keyword: string): void {
        const textToCopy = `{${keyword}}`;
        navigator.clipboard.writeText(textToCopy).then(() => {
            const successMessage = this._translateService.instant('GENERAL.MESSAGE.KEYWORD_COPIED');

            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.fast,
            });
        });
    }

    async changeExperienceLanguage(langId: string) {
        this.selectedExperienceLanguageId = 'translation_' + langId;
    }

    checkValue(langId: string) {
        const languageFormControl = this.formGroup.get('language') as FormArray;
        langId = 'translation_' + langId;

        if (this.experienceTitleTranslations && languageFormControl.controls.length) {
            Object.keys(this.experienceTitleTranslations).forEach((key, index) => {
                this.experienceTitleTranslations[key] = languageFormControl.at(index).get('title').value;
            });
            return this.experienceTitleTranslations[langId];
        } else {
            return false;
        }
    }

    openImageLibraryDialog(): void {
        this.imageLibraryDialogRef = this._matDialog.open(SelectMxImageLibraryDialogComponent, {
            width: '1000px',
            minHeight: 'calc(100vh - 230px)',
            height: 'auto',
            disableClose: true,
        });

        // Keep the 420:240 aspect ratio, but export in recommended resolution to prevent blurry uploads.
        this.imageLibraryDialogRef.componentInstance.previewImageHeight = 617;
        this.imageLibraryDialogRef.componentInstance.previewImageWidth = 1080;

        this.imageLibraryDialogRef.afterClosed().subscribe(async (image: ModuleImage) => {
            if (image) {
                this.isImageUpdated = true;
                this.experience.headerBanner.image = image;
            }
        });
    }

    editDescription(formControl: FormControl): void {
        this.editorDialogRef = this._matDialog.open(MaxBrainEditorDialogComponent, {
            width: '1000px',
            disableClose: true,
        });

        const init: any = this._tinymceService.getInitConfiguration();
        init.plugins = `link`;

        const minNumberOfRows = 6;
        init.min_height = minNumberOfRows * 32 + 20;

        // MX-9333: Jira/ProseMirror paste can leave data-* attrs that break BE translation parsing.
        const originalPastePreprocess = init.paste_preprocess;
        init.paste_preprocess = (plugin, args) => {
            if (originalPastePreprocess) {
                originalPastePreprocess(plugin, args);
            }
            args.content = removeHtmlDataAttributes(args.content);
        };

        const originalPastePostprocess = init.paste_postprocess;
        init.paste_postprocess = (editor, args) => {
            if (originalPastePostprocess) {
                originalPastePostprocess(editor, args);
            }
            removeDataAttributesFromElement(args.node);
        };

        this.editorDialogRef.componentInstance.dialogTitle = 'FORM.EVENT.FIELD.DESCRIPTION';
        this.editorDialogRef.componentInstance.init = init;
        this.editorDialogRef.componentInstance.data = formControl.value;

        this.editorDialogRef
            .afterClosed()
            .pipe(filter((result) => !!result))
            .subscribe((result) => {
                if (result.data !== formControl) {
                    formControl.patchValue(removeHtmlDataAttributes(result.data || ''));
                    this.formGroup.markAsDirty();
                }
            });
    }

    decodeHtmlEntities(label: string): string {
        let txt = document.createElement('textarea');
        txt.innerHTML = label;
        return txt.value;
    }

    async saveChanges() {
        const languageFormControl = this.formGroup.get('language') as FormArray;

        Object.keys(this.experienceTitleTranslations).forEach((key, index) => {
            const titleValue: string = languageFormControl.at(index).get('title').value;
            this.experienceTitleTranslations[key] = titleValue;
        });

        Object.keys(this.experienceDescriptionTranslations).forEach((key, index) => {
            const descriptionValue = languageFormControl.at(index).get('description').value;
            this.experienceDescriptionTranslations[key] = removeHtmlDataAttributes(descriptionValue || '');
        });

        await this._customTranslateService.updateTranslationsByKey(this.experience.headerBanner.title, this.experienceTitleTranslations);
        await this._customTranslateService.updateTranslationsByKey(this.experience.headerBanner.description, this.experienceDescriptionTranslations);

        const experienceUpdated = await this._experienceService.updateExperience(this.experience);
        if (experienceUpdated) {
            this.formGroup.markAsPristine();
            this.isImageUpdated = false;
            const successMessage = this._translateService.instant('ENGAGE.MESSAGE.CHANGES_SAVED');

            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.medium,
            });
        }
    }

    cancelChanges(): void {
        if (this.isDataChanged) {
            this.discardChangesDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
                disableClose: false,
            });

            this.discardChangesDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
            this.discardChangesDialogRef.componentInstance.confirmMessage = 'GENERAL.MESSAGE.DISCARD_CHANGES';

            this.discardChangesDialogRef.afterClosed().subscribe((result) => {
                if (result) {
                    //this.moduleFormService.createFormGroup();
                }
                this.discardChangesDialogRef = null;
            });
        }
    }

    goBack() {
        this._location.back();
    }
}
